import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink} from 'react-router-dom';
import WelcomePage from './components/WelcomePage';
import Calendar from './components/Calendar';
import BookingsList from './components/BookingsList';
import BookingModal from './components/BookingModal';
import AdminMembers from './components/AdminMembers';
import UserSettings from './components/UserSettings';
import OnlineUsers from './components/OnlineUsers';
import api, { setAuthToken, authService, assetService, bookingService } from './services/api';
import AdminAssets from './components/AdminAssets';
import AssetSidebar from './components/AssetSidebar';
import { socketService } from './services/socketService';
import { clientCache } from './services/clientCache';
import './App.css';
import './components/CalendarSidebar.css';

function App() {
  const [user, setUser] = useState(null);
  const titleRef = useRef(null);

  useEffect(() => {
    // Handle magic link token in URL
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setAuthToken(tokenParam);
      localStorage.setItem('token', tokenParam);
      params.delete('token');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // load token and set header
    const token = localStorage.getItem('token');
    if (token) setAuthToken(token);
    api.get('/auth/me')
      .then(res => setUser(res.data.user))
      .catch(() => {});
  }, []);

  // PWA auto-refresh: Force refresh when app is opened/focused
  // useEffect(() => {
  //   const currentBuildTime = Date.now();
  //   const storedBuildTime = localStorage.getItem('app-build-time');
    
  //   // On first load, store current time
  //   if (!storedBuildTime) {
  //     localStorage.setItem('app-build-time', currentBuildTime.toString());
  //     return;
  //   }
    
  //   // Check if app has been closed for more than 1 minute
  //   // const timeDiff = currentBuildTime - parseInt(storedBuildTime);
  //   // const oneMinute = 60 * 1000;
    
  //   // if (timeDiff > oneMinute) {
  //   //   console.log('🔄 PWA reopened after', Math.round(timeDiff / 1000), 'seconds - refreshing...');
  //   //   window.location.reload();
  //   // }
    
  //   // Update the timestamp
  //   localStorage.setItem('app-build-time', currentBuildTime.toString());
    
  //   // Listen for visibility changes (app coming back into focus)
  //   const handleVisibilityChange = () => {
  //     if (!document.hidden) {
  //       const now = Date.now();
  //       const lastFocusTime = localStorage.getItem('app-build-time');
  //       const focusTimeDiff = now - parseInt(lastFocusTime);
        
  //       // If app was in background for more than 1 minute, refresh
  //       if (focusTimeDiff > oneMinute) {
  //         console.log('🔄 PWA refocused after', Math.round(focusTimeDiff / 1000), 'seconds - refreshing...');
  //         window.location.reload();
  //       }
        
  //       localStorage.setItem('app-build-time', now.toString());
  //     }
  //   };
    
  //   document.addEventListener('visibilitychange', handleVisibilityChange);
    
  //   return () => {
  //     document.removeEventListener('visibilitychange', handleVisibilityChange);
  //   };
  // }, []);

  // Shared asset sidebar state
  const [assets, setAssets] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState(() => {
    const saved = localStorage.getItem('selectedAssetIds');
    return saved ? JSON.parse(saved) : [];
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [equipmentType, setEquipmentType] = useState('all');

  // Centralized data management
  const [allBookings, setAllBookings] = useState({}); // Store as { assetId: [bookings] }
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [countdownActive, setCountdownActive] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const dataLoadingRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const hasLoadedInitialDataRef = useRef(false);
  const hasSocketConnectedOnceRef = useRef(false);

  // Centralized modal state management for persistence across tabs
  const [modalState, setModalState] = useState({
    showModal: false,
    modalMode: 'view', // 'create', 'edit', 'view'
    selectedBooking: null,
    isMinimized: false
  });

  // Modal management functions
  const openModal = useCallback((mode, booking = null) => {
    setModalState({
      showModal: true,
      modalMode: mode,
      selectedBooking: booking,
      isMinimized: false
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      showModal: false,
      modalMode: 'view',
      selectedBooking: null,
      isMinimized: false
    });
  }, []);

  const minimizeModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isMinimized: true
    }));
  }, []);

  const expandModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isMinimized: false
    }));
  }, []);

  // Centralized data fetching function
  const loadAllData = useCallback(async (forceRefresh = false) => {
    if (dataLoadingRef.current) return;
    
    dataLoadingRef.current = true;
    setDataLoading(true);
    setDataError(null);
    
    try {
      console.log('Loading all data centrally...', forceRefresh ? '(forcing server fetch)' : '');
      
      const [assetsResponse, bookingsResponse] = await Promise.all([
        assetService.getAll(forceRefresh),
        bookingService.getAll(null, forceRefresh)
      ]);

      const fetchedAssets = assetsResponse.data || [];
      setAssets(fetchedAssets);
      const bookingsData = bookingsResponse.data || {};
      setAllBookings(bookingsData);
      
      if (!hasInitializedRef.current) {
        const savedSelectedAssetIds = localStorage.getItem('selectedAssetIds');
        const savedSidebarCollapsed = localStorage.getItem('sidebarCollapsed');

        if (savedSelectedAssetIds === null) {
          const availableAssetIds = fetchedAssets.filter(a => a.available).map(a => a._id);
          setSelectedAssetIds(availableAssetIds);
        }

        if (savedSidebarCollapsed === null) {
          setSidebarCollapsed(true);
        }

        hasInitializedRef.current = true;
      }

      console.log('Centralized data loaded:', {
        assets: fetchedAssets.length,
        bookings: Object.keys(bookingsData).length
      });
      hasLoadedInitialDataRef.current = true;
      
    } catch (error) {
      console.error('Error loading centralized data:', error);
      setDataError(error.message || 'Failed to load data');
    } finally {
      dataLoadingRef.current = false;
      setDataLoading(false);
    }
  }, []);

  // Function to refresh only bookings data
  const refreshBookings = useCallback(async (forceRefresh = false) => {
    try {
      console.log('🔄 Starting booking refresh...');
      const bookingsResponse = await bookingService.getAll(null, forceRefresh);
      const bookingsData = bookingsResponse.data || {};
      console.log('📊 Received bookings data:', Object.keys(bookingsData).length, 'asset groups');
      setAllBookings(bookingsData);
      
      // Clean up any orphaned bookings in cache
      await clientCache.removeOrphanedBookings(bookingsData);
      
      console.log('✅ Bookings refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing bookings:', error);
      // If there's an error fetching bookings, clear the cache to prevent stale data
      if (error.response?.status >= 500) {
        console.log('🚨 Server error detected, clearing cache to prevent stale data');
        try {
          await clientCache.clearCache();
        } catch (cacheError) {
          console.error('❌ Error clearing cache:', cacheError);
        }
      }
    }
  }, []);

  // Socket event handlers for real-time updates
    const handleSocketBookingCreated = useCallback((data) => {
    console.log('🔄 Socket booking-created received:', data);
    
    if (data.syncId && socketService.hasSyncIdBeenProcessed(data.syncId)) {
      console.log('⚠️ Skipping duplicate booking-created event:', data.syncId);
      return;
    }
    
    if (data.syncId) {
      socketService.markSyncIdAsProcessed(data.syncId);
      console.log('✅ Marked sync ID as processed:', data.syncId);
    }
    
    // If the server provided a booking payload that includes user avatar data
    // apply it locally to avoid overwriting it with a stale server fetch.
    try {
      const bookingPayload = data.booking;
      const assetId = data.assetId && data.assetId.toString();
      const isAvatarUpdate = bookingPayload?.extendedProps?.userAvatar || (data.syncId && data.syncId.startsWith('avatar-update'));

      if (bookingPayload && assetId && isAvatarUpdate) {
        console.log('🔁 Applying booking-created payload locally (avatar update) for asset', assetId);
        setAllBookings(prev => {
          const next = { ...prev };
          const arr = Array.isArray(next[assetId]) ? [...next[assetId]] : [];
          // ensure we don't duplicate
          const idx = arr.findIndex(b => b.id === bookingPayload.id);
          if (idx >= 0) arr[idx] = bookingPayload; else arr.push(bookingPayload);
          next[assetId] = arr;
          // persist merged set into IndexedDB (best-effort)
          try { clientCache.cacheBookings(next); } catch (e) { console.warn('Failed to cache bookings locally:', e); }
          return next;
        });
        return;
      }

      // Force a server refresh so we don't accidentally use stale cached bookings
      console.log('🔄 Refreshing bookings after socket create (force server fetch)...');
      refreshBookings(true);
    } catch (e) {
      console.error('Error handling socket-created payload:', e);
      refreshBookings(true);
    }
  }, [refreshBookings]);

  const handleSocketBookingUpdated = useCallback((data) => {
    console.log('🔄 Socket booking-updated received:', data);
    
    if (data.syncId && socketService.hasSyncIdBeenProcessed(data.syncId)) {
      console.log('⚠️ Skipping duplicate booking-updated event:', data.syncId);
      return;
    }
    
    if (data.syncId) {
      socketService.markSyncIdAsProcessed(data.syncId);
      console.log('✅ Marked sync ID as processed:', data.syncId);
    }
    
    // If the server provided a booking payload that includes user avatar data
    // apply it locally to avoid overwriting it with a stale server fetch.
    try {
      const bookingPayload = data.booking;
      const assetId = data.assetId && data.assetId.toString();
      const isAvatarUpdate = bookingPayload?.extendedProps?.userAvatar || (data.syncId && data.syncId.startsWith('avatar-update'));

      if (bookingPayload && assetId && isAvatarUpdate) {
        console.log('🔁 Applying booking-updated payload locally (avatar update) for asset', assetId);
        setAllBookings(prev => {
          const next = { ...prev };
          const arr = Array.isArray(next[assetId]) ? [...next[assetId]] : [];
          const idx = arr.findIndex(b => b.id === bookingPayload.id);
          if (idx >= 0) arr[idx] = bookingPayload; else arr.push(bookingPayload);
          next[assetId] = arr;
          try { clientCache.cacheBookings(next); } catch (e) { console.warn('Failed to cache bookings locally:', e); }
          return next;
        });
        return;
      }

      // Force a server refresh so we don't accidentally use stale cached bookings
      console.log('🔄 Refreshing bookings after socket update (force server fetch)...');
      refreshBookings(true);
    } catch (e) {
      console.error('Error handling socket-updated payload:', e);
      refreshBookings(true);
    }
  }, [refreshBookings]);

  const handleSocketBookingDeleted = useCallback((data) => {
    console.log('🔄 Socket booking-deleted received:', data);
    
    if (data.syncId && socketService.hasSyncIdBeenProcessed(data.syncId)) {
      console.log('⚠️ Skipping duplicate booking-deleted event:', data.syncId);
      return;
    }
    
    if (data.syncId) {
      socketService.markSyncIdAsProcessed(data.syncId);
      console.log('✅ Marked sync ID as processed:', data.syncId);
    }
    
  // For deletions, a server refresh is the safest option to ensure cache consistency
  console.log('🔄 Refreshing bookings after socket delete (force server fetch)...');
  refreshBookings(true);
  }, [refreshBookings]);

  // Handle mobile Safari visibility changes for socket reconnection
  useEffect(() => {
    if (!user) {
      dataLoadingRef.current = false;
      hasInitializedRef.current = false;
      hasLoadedInitialDataRef.current = false;
      hasSocketConnectedOnceRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && !socketService.isConnected()) {
        console.log('App foregrounded - socket disconnected, force reconnect...');
        socketService.forceReconnect();
      }
    };

    const handleOnline = () => {
      if (!socketService.isConnected()) {
        console.log('Network online - force reconnect socket');
        socketService.forceReconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user]);

  // Setup socket connection and data loading
  useEffect(() => {
    if (!user) return;
    
    // Load initial data first
    // console.log('🚀 User authenticated - loading initial data...');
    loadAllData(true);
    
    // Setup socket connection
    // console.log('🔌 Setting up socket connection...');
    socketService.connect();
    
    // Setup socket event listeners
    const cleanupConnect = socketService.onConnect(() => {
      console.log('Socket connected - refreshing data');
      setIsSocketConnected(true);
      setCountdownActive(false);
      setCountdown(60);

      const isFirstSocketConnect = !hasSocketConnectedOnceRef.current;
      hasSocketConnectedOnceRef.current = true;

      if (!isFirstSocketConnect) {
        setTimeout(() => {
          console.log('🔄 Socket reconnected - refreshing future bookings');
          refreshBookings(true);
        }, 500);
      } else if (!hasLoadedInitialDataRef.current && !dataLoadingRef.current) {
        setTimeout(() => {
          console.log('🔄 Socket connected before initial load completed - loading data');
          loadAllData(true);
        }, 100);
      }
      
      // Join as user when socket connects
      if (user) {
        socketService.joinAsUser(user);
      }
    });
    
    const cleanupDisconnect = socketService.onDisconnect(() => {
      console.log('Socket disconnected');
      setIsSocketConnected(false);
      setCountdownActive(true);
      setCountdown(60);
      setOnlineUsers([]); // Clear online users when disconnected
    });
    
    const cleanupOnlineUsers = socketService.onOnlineUsersUpdated((users) => {
      console.log('Online users updated:', users);
      setOnlineUsers(users || []);
    });
    
    const cleanupBookingCreated = socketService.onBookingCreated(handleSocketBookingCreated);
    const cleanupBookingUpdated = socketService.onBookingUpdated(handleSocketBookingUpdated);
    const cleanupBookingDeleted = socketService.onBookingDeleted(handleSocketBookingDeleted);
    
    const cleanupUserProfileUpdated = socketService.onUserProfileUpdated((data) => {
      console.log('Received user profile update in App:', data);
      
      // Update the current user if it's them
      if (data.user.id === user?.id) {
        setUser(prevUser => ({ ...prevUser, ...data.user }));
      }
      
      // Update the online users list
      setOnlineUsers(prevUsers => 
        prevUsers.map(onlineUser => 
          onlineUser.id === data.user.id 
            ? { ...onlineUser, ...data.user }
            : onlineUser
        )
      );
      
      // No need to refresh bookings - centralized avatar updates handle this automatically
    });
    
    // Join as user immediately if socket is already connected
    if (user && socketService.isConnected()) {
      socketService.joinAsUser(user);
    }
    
    // Cleanup on component unmount
    return () => {
      socketService.leaveAsUser(); // Leave as user when component unmounts
      cleanupConnect();
      cleanupDisconnect();
      cleanupOnlineUsers();
      cleanupUserProfileUpdated();
      cleanupBookingCreated();
      cleanupBookingUpdated();
      cleanupBookingDeleted();
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, handleSocketBookingCreated, handleSocketBookingUpdated, handleSocketBookingDeleted, refreshBookings, loadAllData]);

  // Persist selectedAssetIds
  useEffect(() => {
    localStorage.setItem('selectedAssetIds', JSON.stringify(selectedAssetIds));
  }, [selectedAssetIds]);
  // Persist sidebarCollapsed
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Countdown timer for disconnected state
  useEffect(() => {
    if (!countdownActive) return;
    
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else {
      // countdown reached 0
      setCountdownActive(false);
      setCountdown(60);
    }
  }, [countdownActive, countdown]);

  // Get unique equipment types
  const uniqueEquipmentTypes = [...new Set(assets.map(asset => asset.type || asset.category).filter(Boolean))];

  if (!user) {
    return <WelcomePage onLogin={setUser} />;
  }

  return (
    <Router>
      <div className="app">
        <header className="app-header">
          <div className="header-row-1">
            <div ref={titleRef} className="app-title">
              <img src={require('./components/830437_orig.png')} alt="BKER Logo" style={{ height: 48, verticalAlign: 'middle' }} />
              <span className="title-subtitle">Wingfoil Booking</span>
            </div>
            <div className="connection-container">
              {isSocketConnected ? (
                <OnlineUsers currentUser={user} onlineUsers={onlineUsers} />
              ) : (
                <div className="connection-status disconnected">
                  ● DISCONNECTED - {countdown}s
                </div>
              )}
            </div>
          </div>
          <div className="header-row-2">
            <div className="app-welcome">Welcome {user.name}!</div>
            <div className="header-buttons">
              <button className="refresh-button" onClick={() => loadAllData(true)} title="Refresh data">
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12C21 16.97 16.97 21 12 21C9.69 21 7.58 20.18 6 18.84" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 16L6 18.84L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="settings-button" onClick={() => window.location.href = '/settings'}>
                <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5842 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6642 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.2572 9.77251 19.9887C9.5799 19.7202 9.31074 19.5176 9 19.405C8.69838 19.2719 8.36381 19.2322 8.03941 19.291C7.71502 19.3498 7.41583 19.5045 7.18 19.735L7.12 19.795C6.93425 19.981 6.71368 20.1285 6.47088 20.2291C6.22808 20.3298 5.96783 20.3816 5.705 20.3816C5.44217 20.3816 5.18192 20.3298 4.93912 20.2291C4.69632 20.1285 4.47575 19.981 4.29 19.795C4.10405 19.6093 3.95653 19.3887 3.85588 19.1459C3.75523 18.9031 3.70343 18.6428 3.70343 18.38C3.70343 18.1172 3.75523 17.8569 3.85588 17.6141C3.95653 17.3713 4.10405 17.1507 4.29 16.965L4.35 16.905C4.58054 16.6692 4.73519 16.37 4.794 16.0456C4.85282 15.7212 4.81312 15.3866 4.68 15.085C4.55324 14.7892 4.34276 14.537 4.07447 14.3593C3.80618 14.1816 3.49179 14.0863 3.17 14.085H3C2.46957 14.085 1.96086 13.8743 1.58579 13.4992C1.21071 13.1241 1 12.6154 1 12.085C1 11.5546 1.21071 11.0459 1.58579 10.6708C1.96086 10.2957 2.46957 10.085 3 10.085H3.09C3.42099 10.0773 3.742887 9.97012 4.01138 9.77751C4.27987 9.5849 4.48244 9.31574 4.595 9.005C4.72812 8.70338 4.76782 8.36881 4.709 8.04441C4.65019 7.72002 4.49554 7.42083 4.265 7.185L4.205 7.125C4.01905 6.93925 3.87153 6.71868 3.77088 6.47588C3.67023 6.23308 3.61843 5.97283 3.61843 5.71C3.61843 5.44717 3.67023 5.18692 3.77088 4.94412C3.87153 4.70132 4.01905 4.48075 4.205 4.295C4.39075 4.10905 4.61132 3.96153 4.85412 3.86088C5.09692 3.76023 5.35717 3.70843 5.62 3.70843C5.88283 3.70843 6.14308 3.76023 6.38588 3.86088C6.62868 3.96153 6.84925 4.10905 7.035 4.295L7.095 4.355C7.33083 4.58554 7.63002 4.74019 7.95441 4.799C8.27881 4.85782 8.61338 4.81812 8.915 4.685H9C9.29577 4.55824 9.54802 4.34776 9.72569 4.07947C9.90337 3.81118 9.99872 3.49679 10 3.175V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41279 14.0966 3.72718 14.2743 3.99547C14.452 4.26376 14.7042 4.47424 15 4.607C15.3016 4.74012 15.6362 4.77982 15.9606 4.721C16.285 4.66219 16.5842 4.50754 16.82 4.277L16.88 4.217C17.0657 4.03105 17.2863 3.88353 17.5291 3.78288C17.7719 3.68223 18.0322 3.63043 18.295 3.63043C18.5578 3.63043 18.8181 3.68223 19.0609 3.78288C19.3037 3.88353 19.5243 4.03105 19.71 4.217C19.896 4.40275 20.0435 4.62332 20.1441 4.86612C20.2448 5.10892 20.2966 5.36917 20.2966 5.632C20.2966 5.89483 20.2448 6.15508 20.1441 6.39788C20.0435 6.64068 19.896 6.86125 19.71 7.047L19.65 7.107C19.4195 7.34283 19.2648 7.64202 19.206 7.96641C19.1472 8.29081 19.1869 8.62538 19.32 8.927V9.012C19.4468 9.30777 19.6572 9.56002 19.9255 9.7377C20.1938 9.91537 20.5082 10.0107 20.83 10.012H21C21.5304 10.012 22.0391 10.2227 22.4142 10.5978C22.7893 10.9729 23 11.4816 23 12.012C23 12.5424 22.7893 13.0511 22.4142 13.4262C22.0391 13.8013 21.5304 14.012 21 14.012H20.91C20.5872 14.0133 20.2728 14.1086 20.0045 14.2863C19.7362 14.464 19.5257 14.7162 19.393 15.012V15.012Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="logout-button" onClick={async () => { await authService.logout(); setUser(null); }}>
                Logout
              </button>
            </div>
          </div>
        </header>
        <nav className="nav-tabs">
          <NavLink to="/bookings" end className={({ isActive }) => isActive ? 'active' : ''}>
            Bookings List
          </NavLink>
          <NavLink to="/calendar" className={({ isActive }) => isActive ? 'active' : ''}>
            Calendar View
          </NavLink>
          {user.role === 'admin' && (
            <>
              <NavLink to="/admin/assets" className={({ isActive }) => isActive ? 'active' : ''}>
                Manage Assets
              </NavLink>
              <NavLink to="/admin/members" className={({ isActive }) => isActive ? 'active' : ''}>
                Manage Members
              </NavLink>
            </>
          )}
        </nav>
        <div className="main-layout">
          <div className={`sidebar-container${sidebarCollapsed ? ' collapsed' : ''}`}> 
            <button
              className="sidebar-toggle-btn"
              title={sidebarCollapsed ? 'Expand asset filter' : 'Collapse asset filter'}
              onClick={() => setSidebarCollapsed(c => !c)}
            >
              {/* Standard filter icon using SVG */}
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
              </svg>
            </button>
            {/* AssetSidebar is now persistent and shared */}
            <AssetSidebar
              assets={assets}
              selectedAssetIds={selectedAssetIds}
              onAssetSelectionToggle={id => setSelectedAssetIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])}
              onSelectAll={ids => setSelectedAssetIds(ids)}
              onSelectNone={() => setSelectedAssetIds([])}
              equipmentType={equipmentType}
              onEquipmentTypeChange={setEquipmentType}
              uniqueEquipmentTypes={uniqueEquipmentTypes}
              collapsed={sidebarCollapsed}
            />
          </div>
          <main>
            <Routes>
              <Route path="/" element={
                <BookingsList
                  user={user} 
                  assets={assets} 
                  selectedAssetIds={selectedAssetIds} 
                  setSelectedAssetIds={setSelectedAssetIds} 
                  equipmentType={equipmentType} 
                  setEquipmentType={setEquipmentType} 
                  uniqueEquipmentTypes={uniqueEquipmentTypes}
                  allBookings={allBookings}
                  dataLoading={dataLoading}
                  dataError={dataError}
                  isSocketConnected={isSocketConnected}
                  onRefreshData={loadAllData}
                  modalState={modalState}
                  openModal={openModal}
                  closeModal={closeModal}
                  minimizeModal={minimizeModal}
                  expandModal={expandModal}
              />
              } />
              <Route path="/bookings" element={
                <BookingsList 
                  user={user} 
                  assets={assets} 
                  selectedAssetIds={selectedAssetIds} 
                  setSelectedAssetIds={setSelectedAssetIds} 
                  equipmentType={equipmentType} 
                  setEquipmentType={setEquipmentType} 
                  uniqueEquipmentTypes={uniqueEquipmentTypes}
                  allBookings={allBookings}
                  dataLoading={dataLoading}
                  dataError={dataError}
                  isSocketConnected={isSocketConnected}
                  onRefreshData={loadAllData}
                  modalState={modalState}
                  openModal={openModal}
                  closeModal={closeModal}
                  minimizeModal={minimizeModal}
                  expandModal={expandModal}
                />
              } />
              <Route path="/calendar" element={
                <Calendar 
                  user={user} 
                  assets={assets} 
                  selectedAssetIds={selectedAssetIds} 
                  setSelectedAssetIds={setSelectedAssetIds} 
                  sidebarCollapsed={sidebarCollapsed} 
                  setSidebarCollapsed={setSidebarCollapsed} 
                  equipmentType={equipmentType} 
                  setEquipmentType={setEquipmentType} 
                  uniqueEquipmentTypes={uniqueEquipmentTypes}
                  allBookings={allBookings}
                  dataLoading={dataLoading}
                  dataError={dataError}
                  isSocketConnected={isSocketConnected}
                  onRefreshData={loadAllData}
                  modalState={modalState}
                  openModal={openModal}
                  closeModal={closeModal}
                  minimizeModal={minimizeModal}
                  expandModal={expandModal}
                />
              } />
              <Route path="/settings" element={<UserSettings user={user} onUpdate={setUser} />} />
              {user.role === 'admin' && (
                <>
                  <Route path="/admin/assets" element={<AdminAssets assets={assets} allBookings={allBookings} onAssetsUpdate={setAssets} onBookingsRefreshNeeded={refreshBookings} />} />
                  <Route path="/admin/members" element={<AdminMembers user={user} onUpdate={setUser} allBookings={allBookings} assets={assets} />} />
                </>
              )}
              <Route path="*" element={
                <Calendar 
                  user={user} 
                  assets={assets} 
                  selectedAssetIds={selectedAssetIds} 
                  setSelectedAssetIds={setSelectedAssetIds} 
                  sidebarCollapsed={sidebarCollapsed} 
                  setSidebarCollapsed={setSidebarCollapsed} 
                  equipmentType={equipmentType} 
                  setEquipmentType={setEquipmentType} 
                  uniqueEquipmentTypes={uniqueEquipmentTypes}
                  allBookings={allBookings}
                  dataLoading={dataLoading}
                  dataError={dataError}
                  isSocketConnected={isSocketConnected}
                  onRefreshData={loadAllData}
                  modalState={modalState}
                  openModal={openModal}
                  closeModal={closeModal}
                  minimizeModal={minimizeModal}
                  expandModal={expandModal}
                />
              } />
            </Routes>
          </main>
        </div>
        
        {/* Centralized BookingModal - persists across tab switches */}
        {modalState.showModal && (
          <BookingModal
            user={user}
            booking={modalState.selectedBooking}
            assets={assets}
            allBookings={allBookings}
            mode={modalState.modalMode}
            isMinimized={modalState.isMinimized}
            onClose={closeModal}
            onMinimize={minimizeModal}
            onExpand={expandModal}
            onUpdate={loadAllData}
          />
        )}
      </div>
    </Router>
  );
}

export default App;
